import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  authService,
  checkInData,
  codeService,
  partnerService,
  WorkflowPayload,
  workflowRepo,
} from '../lib/api-services';
import { AuthUser, handleApiError } from '../lib/api-client';
import { RoleFormValues } from '@/components/performerRoles/AddWorkflowPerformerRole';
import { TeamFormValues } from '@/components/performerTeam/AddWorkflowPerformerTeam';

// ==================== AUTH HOOKS ====================
export function useAuthMe(options?: UseQueryOptions<AuthUser, Error>) {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authService.me,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = 'https://example.com/login';
    },
    onError: (error) => {
      console.error('Logout error:', handleApiError(error));
    },
  });
}



// ==================Partners API===============
export function usePartnerData(pageNumber: number, pageSize?: number, partnerName?: string, partnerType?: string, partnerSubType?: string) {
  return useQuery({
    queryKey: ['partners', pageNumber, pageSize, partnerName, partnerType, partnerSubType],
    queryFn: () => partnerService.getPartnersData({
      pageNumber,
      pageSize,
      partnerName,
      partnerType,
      partnerSubType,
    }),
  });
}



// // =================== Audit log Get Hook
// export function useAuditLog(filters?: any) {
//   return useQuery({
//     queryKey: ["auditLog", filters],
//     queryFn: () => userService.getAuditLog(filters),
//   });
// }



// ==================== CODEMANAGER HOOKS ====
export function useTypes(name?: number, types?: string, parentType?: string, parentCode?: string) {
  return useQuery({
    queryKey: ["types", name, types, parentType, parentCode],
    queryFn: () => codeService.listTypes(name, types, parentType, parentCode),
  });
}



// ==================== WORKFLOW REPOSITORY HOOKS ====
export function useWorkflowRepository(page: number = 1, pageSize?: number, intId?: string, intName?: string, intType?: string) {
  return useQuery({
    queryKey: ['workflowRepository', page, pageSize, intId, intName, intType],
    queryFn: () => workflowRepo.getWorkflowData({
      page,
      pageSize,
      intId,
      intName,
      intType,
    }),
  });
}

export function createWorkflowData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WorkflowPayload) => workflowRepo.createWorkflowData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRepository'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function editWorkflowData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: WorkflowPayload }) =>
      workflowRepo.editWorkflowData(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRepository'] });
    },
    onError: (error) => {
      console.error('Edit interface error:', handleApiError(error));
    },
  });
}

export function deleteWorkflowData() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => workflowRepo.deleteWorkflowData(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRepository'] });
    },
    onError: (error) => {
      console.error('Delete interface error:', handleApiError(error));
    },
  });
}

export function toggleWorkflowStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: checkInData }) => workflowRepo.checkInOutWorkflow(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRepository'] });
    },
    onError: (error) => {
      console.error('Toggle interface status error:', handleApiError(error));
    },
  });
}

export function toggleActiveWorkflowStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => workflowRepo.activateInActiveWorkflow(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRepository'] });
    },
    onError: (error) => {
      console.error('Toggle interface status error:', handleApiError(error));
    },
  });
}

export function useWorkflowVersions(Id: number) {
  return useQuery({
    queryKey: ['workflowVersions', Id],
    queryFn: () => workflowRepo.getWorkflowVersionsData(Id),
  });
}

// Workflow Performer Roles
export function useWorkflowPerformerRoles(page: number = 1, pageSize?: number, intId?: string, intName?: string, intType?: string) {
  return useQuery({
    queryKey: ['workflowRole', page, pageSize, intId, intName, intType],
    queryFn: () => workflowRepo.getWorkflowTeamsRole({
      page,
      pageSize,
      intId,
      intName,
      intType,
    }),
  });
}


export function createWorkflowRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RoleFormValues) => workflowRepo.createWorkflowRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRole'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function editWorkflowRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: RoleFormValues }) =>
      workflowRepo.editWorkflowRole(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRole'] });
    },
    onError: (error) => {
      console.error('Edit interface error:', handleApiError(error));
    },
  });
}

export function deleteWorkflowRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => workflowRepo.deleteWorkflowRole(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRole'] });
    },
    onError: (error) => {
      console.error('Delete interface error:', handleApiError(error));
    },
  });
}



// Workflow Performer Teams
export function useWorkflowPerformerTeams(page: number = 1, pageSize?: number, intId?: string, intName?: string, intType?: string) {
  return useQuery({
    queryKey: ['workflowTeams', page, pageSize, intId, intName],
    queryFn: () => workflowRepo.getWorkflowTeamsTeam({
      page,
      pageSize,
      intId,
      intName,
    }),
  });
}

export function createWorkflowTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TeamFormValues) => workflowRepo.createWorkflowTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowTeams'] });
    },
    onError: (error) => {
      console.error('Create user error:', handleApiError(error));
    },
  });
}

export function editWorkflowTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ Id, data }: { Id: number; data: TeamFormValues }) =>
      workflowRepo.editWorkflowTeam(Id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowTeams'] });
    },
    onError: (error) => {
      console.error('Edit interface error:', handleApiError(error));
    },
  });
}

export function deleteWorkflowTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (Id: number) => workflowRepo.deleteWorkflowTeam(Id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowTeams'] });
    },
    onError: (error) => {
      console.error('Delete interface error:', handleApiError(error));
    },
  });
}